import { supabase } from '../lib/supabase';

// Demo mode flag - enables local simulation when backend is unavailable
const DEMO_MODE = true;

// Helper to check if backend is reachable
const isBackendReachable = async () => {
  try {
    const { error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);
    return !error || !error?.message?.includes('Failed to fetch');
  } catch {
    return false;
  }
};

// Generate demo IDs
const generateDemoId = () => `demo_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;

export const authService = {
  async signIn(email, password) {
    try {
      // Check if we should use demo mode
      if (DEMO_MODE && !(await isBackendReachable())) {
        // Demo mode sign in
        return {
          data: {
            user: {
              id: generateDemoId(),
              email: email,
              user_metadata: {
                full_name: 'Demo User',
                role: 'owner'
              }
            },
            session: { access_token: 'demo_token' }
          },
          error: null
        };
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      // If network error in demo mode, return demo data
      if (DEMO_MODE && error?.message?.includes('Failed to fetch')) {
        return {
          data: {
            user: {
              id: generateDemoId(),
              email: email,
              user_metadata: { full_name: 'Demo User', role: 'owner' }
            },
            session: { access_token: 'demo_token' }
          },
          error: null
        };
      }
      return { 
        data: null, 
        error: { message: error?.message || 'Login failed. Please try again.' }
      };
    }
  },

  async signUp(email, password, fullName, role = 'student', additionalData = {}) {
    try {
      // Check if we should use demo mode
      if (DEMO_MODE && !(await isBackendReachable())) {
        return {
          data: {
            user: {
              id: generateDemoId(),
              email: email,
              user_metadata: {
                full_name: fullName,
                role: role,
                phone: additionalData?.phone || ''
              }
            },
            session: { access_token: 'demo_token' }
          },
          error: null
        };
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
            phone: additionalData?.phone || '',
            avatar_url: additionalData?.avatarUrl || ''
          }
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      if (DEMO_MODE && error?.message?.includes('Failed to fetch')) {
        return {
          data: {
            user: {
              id: generateDemoId(),
              email: email,
              user_metadata: { full_name: fullName, role: role }
            },
            session: { access_token: 'demo_token' }
          },
          error: null
        };
      }
      return { 
        data: null, 
        error: { message: error?.message || 'Registration failed. Please try again.' }
      };
    }
  },

  async registerAcademy(academyData) {
    try {
      // Check if backend is reachable, if not use demo mode
      const backendAvailable = await isBackendReachable();
      
      if (DEMO_MODE && !backendAvailable) {
        // Simulate registration process with delay for realistic feel
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const demoUserId = generateDemoId();
        const demoOrgId = generateDemoId();
        
        // Store demo data in localStorage for persistence
        const demoAccount = {
          userId: demoUserId,
          organizationId: demoOrgId,
          email: academyData?.ownerEmail,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          ownerName: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
          createdAt: new Date()?.toISOString(),
          isDemo: true
        };
        
        localStorage.setItem('mydojo_demo_account', JSON.stringify(demoAccount));
        // Set demo mode flags for AuthContext
        localStorage.setItem('demoRegistration', 'true');
        localStorage.setItem('newAcademyName', academyData?.academyName);
        localStorage.setItem('academySubdomain', academyData?.subdomain);
        localStorage.setItem('ownerEmail', academyData?.ownerEmail);
        localStorage.setItem('ownerName', `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`);
        
        return {
          success: true,
          userId: demoUserId,
          organizationId: demoOrgId,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          isDemo: true
        };
      }

      // Step 1: Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: academyData?.ownerEmail,
        password: academyData?.password,
        options: {
          emailRedirectTo: `${window.location?.origin}/login`,
          data: {
            full_name: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
            role: 'owner',
            phone: academyData?.ownerPhone || '',
            academy_name: academyData?.academyName
          }
        }
      });

      if (authError) {
        // If network error in demo mode, fallback to demo registration
        if (DEMO_MODE && authError?.message?.includes('Failed to fetch')) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const demoUserId = generateDemoId();
          const demoOrgId = generateDemoId();
          
          localStorage.setItem('mydojo_demo_account', JSON.stringify({
            userId: demoUserId,
            organizationId: demoOrgId,
            email: academyData?.ownerEmail,
            academyName: academyData?.academyName,
            subdomain: academyData?.subdomain,
            ownerName: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
            createdAt: new Date()?.toISOString(),
            isDemo: true
          }));
          // Set demo mode flags for AuthContext
          localStorage.setItem('demoRegistration', 'true');
          localStorage.setItem('newAcademyName', academyData?.academyName);
          localStorage.setItem('academySubdomain', academyData?.subdomain);
          localStorage.setItem('ownerEmail', academyData?.ownerEmail);
          localStorage.setItem('ownerName', `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`);
          
          return {
            success: true,
            userId: demoUserId,
            organizationId: demoOrgId,
            academyName: academyData?.academyName,
            subdomain: academyData?.subdomain,
            isDemo: true
          };
        }
        
        return { 
          success: false, 
          error: authError?.message,
          errorType: 'AUTH_ERROR'
        };
      }

      if (!authData?.user) {
        return { 
          success: false, 
          error: 'Account creation failed',
          errorType: 'AUTH_ERROR'
        };
      }

      // Step 2: Wait for profile creation with enhanced retry (increased timeout)
      const profileReady = await this.waitForProfile(authData?.user?.id, {
        maxRetries: 40,  // 40 * 600ms = 24 seconds (doubled from 12s)
        retryDelay: 600,
        exponentialBackoff: true
      });

      if (!profileReady?.success) {
        // Fallback: Create profile manually if trigger failed
        console.warn('Trigger timeout - attempting manual profile creation');
        
        const { error: manualProfileError } = await supabase
          ?.from('user_profiles')
          ?.insert({
            id: authData?.user?.id,
            email: authData?.user?.email,
            full_name: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
            role: 'owner',
            phone: academyData?.ownerPhone || null,
            is_active: true
          });

        if (manualProfileError) {
          return {
            success: false,
            error: 'Profile creation failed after multiple attempts',
            errorType: 'PROFILE_TIMEOUT',
            userId: authData?.user?.id,
            canRetry: true
          };
        }
        
        console.log('Manual profile creation successful');
      }

      // Step 3: Create organization using safe function
      const { data: orgResult, error: orgError } = await supabase?.rpc(
        'create_organization_safe',
        {
          org_name: academyData?.academyName,
          org_subdomain: academyData?.subdomain,
          org_owner_id: authData?.user?.id,
          org_settings: {
            address: academyData?.address,
            city: academyData?.city,
            state: academyData?.state,
            zip_code: academyData?.zipCode,
            country: academyData?.country,
            phone: academyData?.phone,
            website: academyData?.website,
            estimated_students: academyData?.estimatedStudents,
            average_monthly_fee: academyData?.averageMonthlyFee
          }
        }
      );

      if (orgError) {
        return {
          success: false,
          error: orgError?.message,
          errorType: 'ORG_CREATION_ERROR',
          userId: authData?.user?.id,
          canRetry: true
        };
      }

      // Check if RPC function returned error
      if (orgResult && orgResult?.length > 0) {
        const result = orgResult?.[0];
        if (!result?.success) {
          return {
            success: false,
            error: result?.error_message || 'Organization creation failed',
            errorType: result?.error_message?.includes('subdomain') ? 'SUBDOMAIN_TAKEN' : 'ORG_CREATION_ERROR',
            userId: authData?.user?.id,
            canRetry: !result?.error_message?.includes('subdomain')
          };
        }

        // Success!
        return {
          success: true,
          userId: authData?.user?.id,
          organizationId: result?.organization_id,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain
        };
      }

      return {
        success: false,
        error: 'Unexpected response from organization creation',
        errorType: 'UNKNOWN_ERROR',
        canRetry: true
      };

    } catch (error) {
      console.error('Academy registration error:', error);
      
      // If network error in demo mode, fallback to demo registration
      if (DEMO_MODE && error?.message?.includes('Failed to fetch')) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const demoUserId = generateDemoId();
        const demoOrgId = generateDemoId();
        
        localStorage.setItem('mydojo_demo_account', JSON.stringify({
          userId: demoUserId,
          organizationId: demoOrgId,
          email: academyData?.ownerEmail,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          ownerName: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
          createdAt: new Date()?.toISOString(),
          isDemo: true
        }));
        
        return {
          success: true,
          userId: demoUserId,
          organizationId: demoOrgId,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          isDemo: true
        };
      }
      
      return {
        success: false,
        error: error?.message || 'Registration failed',
        errorType: 'UNKNOWN_ERROR',
        canRetry: true
      };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      return { error };
    } catch (error) {
      return { error: { message: 'Sign out failed. Please try again.' } };
    }
  },

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      return { user, error };
    } catch (error) {
      return { user: null, error };
    }
  },

  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          *,
          students(*),
          coaches(*)
        `)?.eq('id', userId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to load user profile.' }
      };
    }
  },

  async updateProfile(userId, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to update profile.' }
      };
    }
  },

  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Password reset failed.' } };
    }
  },

  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message || 'Password update failed.' }
      };
    }
  },

  // Wait for user profile to be created by trigger
  async waitForProfile(userId, options = {}) {
    const {
      maxRetries = 20,
      retryDelay = 600,
      exponentialBackoff = true
    } = options;

    let retryCount = 0;
    let currentDelay = retryDelay;
    let lastError = null;

    console.log(`[waitForProfile] Starting profile check for user ${userId}`);

    while (retryCount < maxRetries) {
      try {
        console.log(`[waitForProfile] Attempt ${retryCount + 1}/${maxRetries}`);
        
        const { data: profileData, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('id, email, full_name, role, is_active, created_at')
          ?.eq('id', userId)
          ?.maybeSingle(); // Use maybeSingle instead of single to avoid throwing on no rows

        // Log the response for debugging
        console.log(`[waitForProfile] Response:`, { profileData, profileError });

        if (profileError) {
          console.warn(`[waitForProfile] Query error:`, profileError);
          lastError = profileError;
          // Continue retrying on query errors
        } else if (profileData) {
          // Profile found!
          console.log(`[waitForProfile] ✅ Profile found after ${retryCount + 1} attempts`);
          return { success: true, profile: profileData, attempts: retryCount + 1 };
        } else {
          // No profile yet, but no error
          console.log(`[waitForProfile] Profile not found yet, waiting...`);
          lastError = { message: 'Profile not created yet' };
        }

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, currentDelay));
        
        // Exponential backoff
        if (exponentialBackoff) {
          currentDelay = Math.min(currentDelay * 1.2, 2000);
        }
        
        retryCount++;
      } catch (error) {
        console.error(`[waitForProfile] Exception on attempt ${retryCount + 1}:`, error);
        lastError = error;
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, currentDelay));
      }
    }

    console.error(`[waitForProfile] ❌ Timeout after ${maxRetries} attempts. Last error:`, lastError);

    return {
      success: false,
      error: `Profile not ready after ${maxRetries} attempts (${(maxRetries * retryDelay) / 1000}s)`,
      attempts: retryCount,
      lastError: lastError?.message || 'Unknown error'
    };
  },

  // Get registration status for error recovery
  async getRegistrationStatus(userId) {
    try {
      const { data, error } = await supabase?.rpc('get_registration_status', {
        user_uuid: userId
      });

      if (error) throw error;

      if (data && data?.length > 0) {
        return { success: true, status: data?.[0] };
      }

      return { success: false, error: 'Status not available' };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  },

  // Retry organization creation for existing user
  async retryOrganizationCreation(userId, academyData) {
    try {
      // Check if this is a demo user - if so, return success
      if (DEMO_MODE && (userId?.startsWith('demo_') || !(await isBackendReachable()))) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const demoOrgId = generateDemoId();
        
        localStorage.setItem('mydojo_demo_account', JSON.stringify({
          userId: userId,
          organizationId: demoOrgId,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          createdAt: new Date()?.toISOString(),
          isDemo: true
        }));
        
        return {
          success: true,
          organizationId: demoOrgId,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          isDemo: true
        };
      }

      // Check if profile is ready
      const statusResult = await this.getRegistrationStatus(userId);
      
      if (!statusResult?.success || !statusResult?.status?.can_proceed) {
        return {
          success: false,
          error: 'User profile not ready. Please wait a moment.',
          errorType: 'PROFILE_NOT_READY',
          canRetry: true
        };
      }

      // Try to create organization
      const { data: orgResult, error: orgError } = await supabase?.rpc(
        'create_organization_safe',
        {
          org_name: academyData?.academyName,
          org_subdomain: academyData?.subdomain,
          org_owner_id: userId,
          org_settings: {
            address: academyData?.address,
            city: academyData?.city,
            state: academyData?.state,
            zip_code: academyData?.zipCode,
            country: academyData?.country,
            phone: academyData?.phone,
            website: academyData?.website,
            estimated_students: academyData?.estimatedStudents,
            average_monthly_fee: academyData?.averageMonthlyFee
          }
        }
      );

      // Handle RPC call error
      if (orgError) {
        console.error('Retry organization RPC error:', orgError);
        
        // Fallback to demo mode if network error
        if (DEMO_MODE && orgError?.message?.includes('Failed to fetch')) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const demoOrgId = generateDemoId();
          return {
            success: true,
            organizationId: demoOrgId,
            academyName: academyData?.academyName,
            subdomain: academyData?.subdomain,
            isDemo: true
          };
        }
        
        return {
          success: false,
          error: orgError?.message || 'Failed to create organization',
          errorType: 'ORG_CREATION_ERROR',
          canRetry: true
        };
      }

      // Handle empty or invalid result
      if (!orgResult || !Array.isArray(orgResult) || orgResult?.length === 0) {
        console.error('Invalid retry organization result:', orgResult);
        return {
          success: false,
          error: 'Organization creation returned no data. Please try again.',
          errorType: 'ORG_CREATION_ERROR',
          canRetry: true
        };
      }

      // Check function result
      const result = orgResult?.[0];
      if (!result?.success) {
        return {
          success: false,
          error: result?.error_message || 'Organization creation failed',
          errorType: 'ORG_CREATION_ERROR',
          canRetry: true
        };
      }

      return {
        success: true,
        organizationId: result?.organization_id,
        academyName: academyData?.academyName,
        subdomain: academyData?.subdomain
      };

    } catch (error) {
      console.error('Retry organization creation error:', error);
      
      // Fallback to demo mode if network error
      if (DEMO_MODE && error?.message?.includes('Failed to fetch')) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const demoOrgId = generateDemoId();
        return {
          success: true,
          organizationId: demoOrgId,
          academyName: academyData?.academyName,
          subdomain: academyData?.subdomain,
          isDemo: true
        };
      }
      
      return {
        success: false,
        error: error?.message || 'Retry failed. Please try again.',
        errorType: 'UNKNOWN_ERROR',
        canRetry: true
      };
    }
  },

  // Resend confirmation email
  async resendConfirmationEmail(email) {
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email
      });

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to resend confirmation email'
      };
    }
  },

  // Check email confirmation status
  async checkEmailConfirmation(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, email')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      // Check auth.users for email_confirmed_at
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();

      if (authError) throw authError;

      return {
        success: true,
        confirmed: !!user?.email_confirmed_at,
        confirmedAt: user?.email_confirmed_at
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message,
        confirmed: false
      };
    }
  },

  // Manual profile creation fallback
  async createProfileManually(userId, profileData) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.insert([{
          id: userId,
          email: profileData?.email,
          full_name: profileData?.fullName,
          role: profileData?.role || 'owner',
          phone: profileData?.phone || '',
          is_active: true
        }])
        ?.select()
        ?.single();

      if (error) {
        // If conflict, try to update instead
        if (error?.code === '23505') {
          const { data: updateData, error: updateError } = await supabase
            ?.from('user_profiles')
            ?.update({
              full_name: profileData?.fullName,
              phone: profileData?.phone || '',
              is_active: true,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', userId)
            ?.select()
            ?.single();

          if (updateError) throw updateError;
          return { success: true, data: updateData };
        }
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Failed to create profile'
      };
    }
  },

  // Comprehensive registration recovery
  async recoverRegistration(email, password) {
    try {
      // Try to sign in first
      const signInResult = await this.signIn(email, password);
      
      if (signInResult?.data?.user) {
        // User exists and can sign in
        const userId = signInResult?.data?.user?.id;
        
        // Check registration status
        const statusResult = await this.getRegistrationStatus(userId);
        
        if (statusResult?.success && statusResult?.status) {
          return {
            success: true,
            recoveryType: 'SIGN_IN',
            userId: userId,
            status: statusResult?.status,
            message: 'Successfully signed in. Checking registration status...'
          };
        }
        
        return {
          success: true,
          recoveryType: 'SIGN_IN',
          userId: userId,
          message: 'Successfully signed in'
        };
      }

      // Sign in failed, account might not exist
      return {
        success: false,
        error: 'Account not found or invalid credentials',
        recoveryType: 'NONE'
      };

    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Recovery failed',
        recoveryType: 'NONE'
      };
    }
  },

  // Complete registration recovery with organization creation
  async completeRegistrationRecovery(userId, academyData) {
    try {
      // Step 1: Ensure profile exists
      const statusResult = await this.getRegistrationStatus(userId);
      
      if (!statusResult?.success) {
        // Try to create profile manually
        const profileResult = await this.createProfileManually(userId, {
          email: academyData?.ownerEmail,
          fullName: `${academyData?.ownerFirstName} ${academyData?.ownerLastName}`,
          role: 'owner',
          phone: academyData?.ownerPhone
        });

        if (!profileResult?.success) {
          return {
            success: false,
            error: 'Failed to create user profile',
            errorType: 'PROFILE_CREATION_FAILED'
          };
        }

        // Wait a moment for profile to be fully ready
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Step 2: Check if organization already exists
      const { data: existingOrg, error: orgCheckError } = await supabase
        ?.from('organizations')
        ?.select('id, name, subdomain')
        ?.eq('owner_id', userId)
        ?.single();

      if (existingOrg && !orgCheckError) {
        return {
          success: true,
          organizationId: existingOrg?.id,
          academyName: existingOrg?.name,
          subdomain: existingOrg?.subdomain,
          message: 'Organization already exists'
        };
      }

      // Step 3: Create organization
      const orgResult = await this.retryOrganizationCreation(userId, academyData);
      
      return orgResult;

    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Recovery failed',
        errorType: 'RECOVERY_FAILED'
      };
    }
  }
};